import logger from '../../utils/logger';
import FinanceReportsTab from './tabs/FinanceReportsTab';
import { FinanceDashboardTab, FinanceExpensesTab, FinanceReimbursementsTab, StatusBadge, EXPENSE_CATEGORIES, PAYMENT_METHODS, REIMB_STATUSES } from './tabs/FinanceDataTabs';
import { FinanceTripsTab, FinanceTeamTab, FinanceSubmissionsTab } from './tabs/FinanceTripTeamTabs';
import ChartOfAccountsTab from './tabs/ChartOfAccountsTab';
import TreasurerDashboardTab from './tabs/TreasurerDashboardTab';
import JournalEntriesTab from './tabs/JournalEntriesTab';
import BalanceSheetTab from './tabs/BalanceSheetTab';
import FundsTab from './tabs/FundsTab';
import UnifiedDonationsLedgerTab from './tabs/UnifiedDonationsLedgerTab';
import StatementOfActivitiesTab from './tabs/StatementOfActivitiesTab';
import ExportsReportsTab from './tabs/ExportsReportsTab';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  LayoutDashboard, DollarSign, Receipt, Car, Users, ClipboardList,
  LogOut, Menu, X, Loader2, FileText, ArrowLeft, BarChart3, BookOpen, PieChart, Scale, NotebookPen,
  Heart, Coins, FileBarChart2, FileSpreadsheet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RECEIPT_FILENAME_MAX_LENGTH = 15;

const AdminFinance = () => {
  const { user, logout, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  // Dashboard data
  const [dashData, setDashData] = useState(null);
  const [financeDateFilter, setFinanceDateFilter] = useState({ from: '', to: '' });

  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0], paid_by: '', category: 'Travel',
    account_code: '',
    description: '', amount: 0, payment_method: 'Cash', reimbursed: 'not_reimbursed',
    funding_source: '', trip_id: '', notes: '',
    fund_id: '', campaign_id: '',
  });
  const [expenseFilters, setExpenseFilters] = useState({ search: '', category: '', reimbursed: '' });
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(null);

  // Reimbursements state
  const [reimbursements, setReimbursements] = useState([]);
  const [editingReimb, setEditingReimb] = useState(null);
  const [reimbForm, setReimbForm] = useState({
    person_name: '', related_expense_ids: [], related_trip_id: '',
    total_amount: 0, status: 'owed', date_paid: '', payment_notes: ''
  });
  const [showReimbForm, setShowReimbForm] = useState(false);
  const [reimbFilters, setReimbFilters] = useState({ search: '', status: '' });

  // Trips state
  const [trips, setTrips] = useState([]);
  const [editingTrip, setEditingTrip] = useState(null);
  const [tripForm, setTripForm] = useState({
    date: new Date().toISOString().split('T')[0], destination: '', starting_location: '',
    purpose: '', miles_traveled: 0, people_involved: [],
    visit_notes: '', meeting_notes: '', needs_identified: '', mission_support: '',
    reimbursement_owed: false, notes: ''
  });
  const [showTripForm, setShowTripForm] = useState(false);
  const [tripFilters, setTripFilters] = useState({ search: '', date_from: '', date_to: '' });
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [peopleInput, setPeopleInput] = useState('');

  // Team members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', email: '', password: '' });

  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [submissionFilters, setSubmissionFilters] = useState({ status: '' });
  const [reviewingSubmission, setReviewingSubmission] = useState(null);
  const [reviewForm, setReviewForm] = useState({ status: 'approved', review_notes: '' });

  const [saving, setSaving] = useState(false);

  // Reports state
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState('monthly');
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate('/admin/login');
    else if (!authLoading && user && !isAdmin) navigate('/');
  }, [user, isAdmin, authLoading, navigate]);

  const fetchDashboard = useCallback(async (dateFrom, dateTo) => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${BACKEND_URL}/api/admin/finance/dashboard`, { params, withCredentials: true });
      setDashData(res.data);
    } catch (e) { logger.error('Dashboard fetch error', e); }
  }, []);

  const fetchExpenses = useCallback(async () => {
    try {
      const params = {};
      if (expenseFilters.search) params.search = expenseFilters.search;
      if (expenseFilters.category) params.category = expenseFilters.category;
      if (expenseFilters.reimbursed) params.reimbursed = expenseFilters.reimbursed;
      const res = await axios.get(`${BACKEND_URL}/api/admin/finance/expenses`, { params, withCredentials: true });
      setExpenses(res.data);
    } catch (e) { logger.error(e); }
  }, [expenseFilters]);

  const fetchReimbursements = useCallback(async () => {
    try {
      const params = {};
      if (reimbFilters.search) params.search = reimbFilters.search;
      if (reimbFilters.status) params.status = reimbFilters.status;
      const res = await axios.get(`${BACKEND_URL}/api/admin/finance/reimbursements`, { params, withCredentials: true });
      setReimbursements(res.data);
    } catch (e) { logger.error(e); }
  }, [reimbFilters]);

  const fetchTrips = useCallback(async () => {
    try {
      const params = {};
      if (tripFilters.search) params.search = tripFilters.search;
      if (tripFilters.date_from) params.date_from = tripFilters.date_from;
      if (tripFilters.date_to) params.date_to = tripFilters.date_to;
      const res = await axios.get(`${BACKEND_URL}/api/admin/finance/trips`, { params, withCredentials: true });
      setTrips(res.data);
    } catch (e) { logger.error(e); }
  }, [tripFilters]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/finance/team-members`, { withCredentials: true });
      setTeamMembers(res.data);
    } catch (e) { logger.error(e); }
  }, []);

  const fetchSubmissions = useCallback(async () => {
    try {
      const params = {};
      if (submissionFilters.status) params.status = submissionFilters.status;
      const res = await axios.get(`${BACKEND_URL}/api/admin/finance/submissions`, { params, withCredentials: true });
      setSubmissions(res.data);
    } catch (e) { logger.error(e); }
  }, [submissionFilters]);

  useEffect(() => {
    if (user && isAdmin) {
      setLoading(true);
      Promise.all([fetchDashboard('', ''), fetchExpenses(), fetchReimbursements(), fetchTrips(), fetchTeamMembers(), fetchSubmissions()])
        .finally(() => setLoading(false));
    }
  }, [user, isAdmin, fetchDashboard, fetchExpenses, fetchReimbursements, fetchTrips, fetchTeamMembers, fetchSubmissions]);

  const handleDashboardFilterApply = useCallback(() => {
    fetchDashboard(financeDateFilter.from, financeDateFilter.to);
  }, [fetchDashboard, financeDateFilter]);

  useEffect(() => { if (user && isAdmin && activeTab === 'expenses') fetchExpenses(); }, [expenseFilters, user, isAdmin, activeTab, fetchExpenses]);
  useEffect(() => { if (user && isAdmin && activeTab === 'reimbursements') fetchReimbursements(); }, [reimbFilters, user, isAdmin, activeTab, fetchReimbursements]);
  useEffect(() => { if (user && isAdmin && activeTab === 'trips') fetchTrips(); }, [tripFilters, user, isAdmin, activeTab, fetchTrips]);
  useEffect(() => { if (user && isAdmin && activeTab === 'submissions') fetchSubmissions(); }, [submissionFilters, user, isAdmin, activeTab, fetchSubmissions]);

  const fetchReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const endpoint = reportType === 'monthly'
        ? `${BACKEND_URL}/api/admin/finance/report/monthly?year=${reportYear}&month=${reportMonth}`
        : `${BACKEND_URL}/api/admin/finance/report/annual?year=${reportYear}`;
      const res = await axios.get(endpoint, { withCredentials: true });
      setReportData(res.data);
    } catch (e) { logger.error(e); }
    setReportLoading(false);
  }, [reportType, reportYear, reportMonth]);

  useEffect(() => { if (user && isAdmin && activeTab === 'reports') fetchReport(); }, [activeTab, reportType, reportYear, reportMonth, user, isAdmin, fetchReport]);

  // ==================== EXPENSE HANDLERS ====================
  const resetExpenseForm = useCallback(() => {
    setExpenseForm({ date: new Date().toISOString().split('T')[0], paid_by: '', category: 'Travel', account_code: '', description: '', amount: 0, payment_method: 'Cash', reimbursed: 'not_reimbursed', funding_source: '', trip_id: '', notes: '', fund_id: '', campaign_id: '' });
    setEditingExpense(null);
    setShowExpenseForm(false);
  }, []);

  const handleSaveExpense = useCallback(async () => {
    if (!expenseForm.date || !expenseForm.paid_by || !expenseForm.description || !expenseForm.amount) return;
    if (!expenseForm.account_code) {
      alert('Please pick a 4-digit account code before saving.');
      return;
    }
    setSaving(true);
    try {
      if (editingExpense) {
        await axios.put(`${BACKEND_URL}/api/admin/finance/expenses/${editingExpense}`, expenseForm, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/finance/expenses`, expenseForm, { withCredentials: true });
      }
      resetExpenseForm();
      fetchExpenses();
      fetchDashboard();
    } catch (e) { logger.error(e); }
    setSaving(false);
  }, [expenseForm, editingExpense, resetExpenseForm, fetchExpenses, fetchDashboard]);

  const handleEditExpense = useCallback((exp) => {
    setExpenseForm({ date: exp.date, paid_by: exp.paid_by, category: exp.category, account_code: exp.account_code || '', description: exp.description, amount: exp.amount, payment_method: exp.payment_method, reimbursed: exp.reimbursed, funding_source: exp.funding_source || '', trip_id: exp.trip_id || '', notes: exp.notes || '' });
    setEditingExpense(exp.id);
    setShowExpenseForm(true);
  }, []);

  const handleDeleteExpense = useCallback(async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/finance/expenses/${id}`, { withCredentials: true });
    fetchExpenses();
    fetchDashboard();
  }, [fetchExpenses, fetchDashboard]);

  const handleReceiptUpload = useCallback(async (expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    setUploadingReceipt(expenseId);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/finance/expenses/${expenseId}/receipt`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      fetchExpenses();
    } catch (e) { logger.error(e); }
    setUploadingReceipt(null);
  }, [fetchExpenses]);

  // ==================== REIMBURSEMENT HANDLERS ====================
  const resetReimbForm = useCallback(() => {
    setReimbForm({ person_name: '', related_expense_ids: [], related_trip_id: '', total_amount: 0, status: 'owed', date_paid: '', payment_notes: '' });
    setEditingReimb(null);
    setShowReimbForm(false);
  }, []);

  const handleSaveReimb = useCallback(async () => {
    if (!reimbForm.person_name || !reimbForm.total_amount) return;
    setSaving(true);
    try {
      if (editingReimb) {
        await axios.put(`${BACKEND_URL}/api/admin/finance/reimbursements/${editingReimb}`, reimbForm, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/finance/reimbursements`, reimbForm, { withCredentials: true });
      }
      resetReimbForm();
      fetchReimbursements();
      fetchDashboard();
    } catch (e) { logger.error(e); }
    setSaving(false);
  }, [reimbForm, editingReimb, resetReimbForm, fetchReimbursements, fetchDashboard]);

  const handleEditReimb = useCallback((r) => {
    setReimbForm({ person_name: r.person_name, related_expense_ids: r.related_expense_ids || [], related_trip_id: r.related_trip_id || '', total_amount: r.total_amount, status: r.status, date_paid: r.date_paid || '', payment_notes: r.payment_notes || '' });
    setEditingReimb(r.id);
    setShowReimbForm(true);
  }, []);

  const handleDeleteReimb = useCallback(async (id) => {
    if (!window.confirm('Delete this reimbursement?')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/finance/reimbursements/${id}`, { withCredentials: true });
    fetchReimbursements();
    fetchDashboard();
  }, [fetchReimbursements, fetchDashboard]);

  // ==================== TRIP HANDLERS ====================
  const resetTripForm = useCallback(() => {
    setTripForm({ date: new Date().toISOString().split('T')[0], destination: '', starting_location: '', purpose: '', miles_traveled: 0, people_involved: [], visit_notes: '', meeting_notes: '', needs_identified: '', mission_support: '', reimbursement_owed: false, notes: '' });
    setPeopleInput('');
    setEditingTrip(null);
    setShowTripForm(false);
  }, []);

  const handleSaveTrip = useCallback(async () => {
    if (!tripForm.date || !tripForm.destination || !tripForm.purpose) return;
    setSaving(true);
    try {
      const payload = { ...tripForm };
      if (editingTrip) {
        await axios.put(`${BACKEND_URL}/api/admin/finance/trips/${editingTrip}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/finance/trips`, payload, { withCredentials: true });
      }
      resetTripForm();
      fetchTrips();
      fetchDashboard();
    } catch (e) { logger.error(e); }
    setSaving(false);
  }, [tripForm, editingTrip, resetTripForm, fetchTrips, fetchDashboard]);

  const handleEditTrip = useCallback((t) => {
    setTripForm({ date: t.date, destination: t.destination, starting_location: t.starting_location || '', purpose: t.purpose, miles_traveled: t.miles_traveled || 0, people_involved: t.people_involved || [], visit_notes: t.visit_notes || '', meeting_notes: t.meeting_notes || '', needs_identified: t.needs_identified || '', mission_support: t.mission_support || '', reimbursement_owed: t.reimbursement_owed || false, notes: t.notes || '' });
    setPeopleInput((t.people_involved || []).join(', '));
    setEditingTrip(t.id);
    setShowTripForm(true);
  }, []);

  const handleDeleteTrip = useCallback(async (id) => {
    if (!window.confirm('Delete this trip?')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/finance/trips/${id}`, { withCredentials: true });
    fetchTrips();
    fetchDashboard();
  }, [fetchTrips, fetchDashboard]);

  const handleTripReceiptUpload = useCallback(async (tripId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/finance/trips/${tripId}/receipt`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      fetchTrips();
    } catch (e) { logger.error(e); }
  }, [fetchTrips]);

  // ==================== TEAM MEMBER HANDLERS ====================
  const handleCreateTeamMember = useCallback(async () => {
    if (!teamForm.name || !teamForm.email || !teamForm.password) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/finance/team-members`, teamForm, { withCredentials: true });
      setTeamForm({ name: '', email: '', password: '' });
      setShowTeamForm(false);
      fetchTeamMembers();
      fetchDashboard();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to create team member'); }
    setSaving(false);
  }, [teamForm, fetchTeamMembers, fetchDashboard]);

  const handleToggleTeamMember = useCallback(async (memberId, isActive) => {
    await axios.put(`${BACKEND_URL}/api/admin/finance/team-members/${memberId}`, { is_active: !isActive }, { withCredentials: true });
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const handleDeleteTeamMember = useCallback(async (id) => {
    if (!window.confirm('Delete this team member? They will lose access.')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/finance/team-members/${id}`, { withCredentials: true });
    fetchTeamMembers();
    fetchDashboard();
  }, [fetchTeamMembers, fetchDashboard]);

  // ==================== SUBMISSION HANDLERS ====================
  const handleReviewSubmission = useCallback(async () => {
    if (!reviewingSubmission) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/finance/submissions/${reviewingSubmission}/review`, reviewForm, { withCredentials: true });
      setReviewingSubmission(null);
      setReviewForm({ status: 'approved', review_notes: '' });
      fetchSubmissions();
      fetchDashboard();
      fetchExpenses();
      fetchTrips();
    } catch (e) { logger.error(e); }
    setSaving(false);
  }, [reviewingSubmission, reviewForm, fetchSubmissions, fetchDashboard, fetchExpenses, fetchTrips]);

  // ==================== EXPORT HANDLERS ====================
  const handleExport = useCallback(async (type) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/finance/export/${type}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { logger.error(e); }
  }, []);

  const handleLogout = useCallback(async () => { await logout(); navigate('/admin/login'); }, [logout, navigate]);

  const navItems = [
    { id: 'dashboard', label: 'Finance Overview', icon: LayoutDashboard },
    { id: 'treasurer', label: 'Treasurer Report', icon: PieChart },
    { id: 'statement-of-activities', label: 'Statement of Activities', icon: FileBarChart2 },
    { id: 'donations-ledger', label: 'Donations Ledger', icon: Heart },
    { id: 'balance-sheet', label: 'Balance Sheet', icon: Scale },
    { id: 'journal-entries', label: 'Journal Entries', icon: NotebookPen },
    { id: 'funds', label: 'Funds', icon: Coins },
    { id: 'expenses', label: 'Expenses', icon: Receipt },
    { id: 'reimbursements', label: 'Reimbursements', icon: DollarSign },
    { id: 'trips', label: 'Travel Log', icon: Car },
    { id: 'reports', label: 'Reports', icon: BarChart3 },
    { id: 'exports', label: 'Exports & Reports', icon: FileSpreadsheet },
    { id: 'chart-of-accounts', label: 'Chart of Accounts', icon: BookOpen },
    { id: 'team', label: 'Team Members', icon: Users },
    { id: 'submissions', label: 'Submissions', icon: ClipboardList },
  ];

  const sortedCategoryTotals = useMemo(() => {
    if (!dashData?.category_totals) return [];
    return Object.entries(dashData.category_totals).sort((a, b) => b[1] - a[1]);
  }, [dashData]);

  // ==================== Memoized ctx objects ====================

  const expensesCtx = useMemo(() => ({
    expenses, expenseFilters, setExpenseFilters, showExpenseForm, setShowExpenseForm,
    expenseForm, setExpenseForm, editingExpense, resetExpenseForm, handleSaveExpense,
    handleEditExpense, handleDeleteExpense, handleReceiptUpload, uploadingReceipt,
    handleExport, trips, saving, inputClass: "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:ring-1 focus:ring-[#BA1826]/20 outline-none",
    labelClass: "block text-xs font-medium text-[#4B5563] mb-1"
  }), [expenses, expenseFilters, showExpenseForm, expenseForm, editingExpense, resetExpenseForm,
    handleSaveExpense, handleEditExpense, handleDeleteExpense, handleReceiptUpload, uploadingReceipt,
    handleExport, trips, saving]);

  const reimbCtx = useMemo(() => ({
    reimbursements, reimbFilters, setReimbFilters, showReimbForm, setShowReimbForm,
    reimbForm, setReimbForm, editingReimb, resetReimbForm, handleSaveReimb,
    handleEditReimb, handleDeleteReimb, handleExport, trips, expenses,
    saving, inputClass: "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:ring-1 focus:ring-[#BA1826]/20 outline-none",
    labelClass: "block text-xs font-medium text-[#4B5563] mb-1"
  }), [reimbursements, reimbFilters, showReimbForm, reimbForm, editingReimb, resetReimbForm,
    handleSaveReimb, handleEditReimb, handleDeleteReimb, handleExport, trips, expenses, saving]);

  const tripsCtx = useMemo(() => ({
    trips, tripFilters, setTripFilters, showTripForm, setShowTripForm,
    tripForm, setTripForm, editingTrip, resetTripForm, handleSaveTrip,
    handleEditTrip, handleDeleteTrip, handleTripReceiptUpload, handleExport,
    expandedTrip, setExpandedTrip, peopleInput, setPeopleInput,
    saving, inputClass: "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:ring-1 focus:ring-[#BA1826]/20 outline-none",
    labelClass: "block text-xs font-medium text-[#4B5563] mb-1"
  }), [trips, tripFilters, showTripForm, tripForm, editingTrip, resetTripForm,
    handleSaveTrip, handleEditTrip, handleDeleteTrip, handleTripReceiptUpload, handleExport,
    expandedTrip, peopleInput, saving]);

  const teamCtx = useMemo(() => ({
    teamMembers, showTeamForm, setShowTeamForm, teamForm, setTeamForm,
    handleCreateTeamMember, handleToggleTeamMember, handleDeleteTeamMember,
    saving, inputClass: "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:ring-1 focus:ring-[#BA1826]/20 outline-none",
    labelClass: "block text-xs font-medium text-[#4B5563] mb-1"
  }), [teamMembers, showTeamForm, teamForm, handleCreateTeamMember, handleToggleTeamMember,
    handleDeleteTeamMember, saving]);

  const submissionsCtx = useMemo(() => ({
    submissions, submissionFilters, setSubmissionFilters,
    reviewingSubmission, setReviewingSubmission, reviewForm, setReviewForm,
    handleReviewSubmission, saving, inputClass: "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:ring-1 focus:ring-[#BA1826]/20 outline-none",
    labelClass: "block text-xs font-medium text-[#4B5563] mb-1"
  }), [submissions, submissionFilters, reviewingSubmission, reviewForm, handleReviewSubmission, saving]);

  const reportsCtx = useMemo(() => ({
    reportType, setReportType, reportYear, setReportYear,
    reportMonth, setReportMonth, reportData, setReportData, reportLoading, fetchReport
  }), [reportType, reportYear, reportMonth, reportData, reportLoading, fetchReport]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:ring-1 focus:ring-[#BA1826]/20 outline-none";
  const labelClass = "block text-xs font-medium text-[#4B5563] mb-1";

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex" data-testid="admin-finance">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="shrink-0 flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#BA1826]" />
            <span className="font-heading font-bold text-white text-sm">Finance & Docs</span>
          </div>
          <button onClick={() => setMobileMenuOpen(false)} className="lg:hidden text-gray-400">
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-1 nhf-sidebar-scroll">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
              data-testid={`nav-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === item.id ? 'bg-[#BA1826] text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              <span className="truncate">{item.label}</span>
              {item.id === 'submissions' && dashData?.pending_submissions > 0 && (
                <span className="ml-auto bg-yellow-500 text-black text-xs px-1.5 py-0.5 rounded-full font-bold">{dashData.pending_submissions}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="shrink-0 p-4 border-t border-gray-800 space-y-1 bg-[#0A0A0A]">
          <Link to="/admin/dashboard" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 shrink-0" /> <span className="truncate">Main Dashboard</span>
          </Link>
          <Link to={`/admin/help?topic=${
            activeTab === 'treasurer' ? 'treasurer_report' :
            activeTab === 'statement-of-activities' ? 'statement_of_activities' :
            activeTab === 'donations-ledger' ? 'donations_ledger' :
            activeTab === 'balance-sheet' ? 'balance_sheet' :
            activeTab === 'journal-entries' ? 'journal_entries' :
            activeTab === 'funds' ? 'funds_overview' :
            activeTab === 'chart-of-accounts' ? 'chart_of_accounts' :
            'finance_overview'
          }`}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="finance-help-link">
            <BookOpen className="w-4 h-4 shrink-0" /> <span className="truncate">Help for this view</span>
          </Link>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
            <LogOut className="w-4 h-4 shrink-0" /> <span className="truncate">Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        <header className="bg-white border-b border-[#E5E7EB] px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden"><Menu className="w-6 h-6" /></button>
            <h1 className="font-heading text-xl font-bold text-[#0A0A0A]" data-testid="finance-page-title">
              {navItems.find(n => n.id === activeTab)?.label || 'Finance'}
            </h1>
          </div>
          <Link to="/admin/dashboard" className="text-sm text-[#4B5563] hover:text-[#BA1826]">Main Dashboard</Link>
        </header>

        <main className="p-4 md:p-6">

          {/* ==================== DASHBOARD TAB ==================== */}
          {activeTab === 'dashboard' && dashData && (
            <FinanceDashboardTab dashData={dashData} sortedCategoryTotals={sortedCategoryTotals}
              dateFilter={financeDateFilter} setDateFilter={setFinanceDateFilter} onFilterApply={handleDashboardFilterApply} />
          )}

          {/* ==================== EXPENSES TAB ==================== */}
          {activeTab === 'expenses' && (
            <FinanceExpensesTab ctx={expensesCtx} />
          )}

          {/* ==================== REIMBURSEMENTS TAB ==================== */}
          {activeTab === 'reimbursements' && (
            <FinanceReimbursementsTab ctx={reimbCtx} />
          )}

          {/* ==================== TRIPS TAB ==================== */}
          {activeTab === 'trips' && (
            <FinanceTripsTab ctx={tripsCtx} />
          )}

          {/* ==================== TEAM MEMBERS TAB ==================== */}
          {activeTab === 'team' && (
            <FinanceTeamTab ctx={teamCtx} />
          )}

          {/* ==================== SUBMISSIONS TAB ==================== */}
          {activeTab === 'submissions' && (
            <FinanceSubmissionsTab ctx={submissionsCtx} />
          )}

          {/* ==================== REPORTS TAB ==================== */}
          {activeTab === 'reports' && (
            <FinanceReportsTab ctx={reportsCtx} />
          )}

          {/* ==================== TREASURER REPORT TAB ==================== */}
          {activeTab === 'treasurer' && (
            <TreasurerDashboardTab />
          )}

          {/* ==================== STATEMENT OF ACTIVITIES TAB ==================== */}
          {activeTab === 'statement-of-activities' && (
            <StatementOfActivitiesTab />
          )}

          {/* ==================== UNIFIED DONATIONS LEDGER TAB ==================== */}
          {activeTab === 'donations-ledger' && (
            <UnifiedDonationsLedgerTab />
          )}

          {/* ==================== BALANCE SHEET TAB ==================== */}
          {activeTab === 'balance-sheet' && (
            <BalanceSheetTab />
          )}

          {/* ==================== JOURNAL ENTRIES TAB ==================== */}
          {activeTab === 'journal-entries' && (
            <JournalEntriesTab />
          )}

          {/* ==================== FUNDS TAB ==================== */}
          {activeTab === 'funds' && (
            <FundsTab />
          )}

          {/* ==================== CHART OF ACCOUNTS TAB ==================== */}
          {activeTab === 'chart-of-accounts' && (
            <ChartOfAccountsTab />
          )}

          {/* ==================== EXPORTS & REPORTS TAB ==================== */}
          {activeTab === 'exports' && (
            <ExportsReportsTab />
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminFinance;
